/**
 * GMaps Place picker hooks — server-proxied to keep API key out of the SPA.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { env } from './env';

export interface GmapsPrediction {
  place_id: string;
  description: string;
  structured_formatting: { main_text: string; secondary_text?: string };
}

export interface GmapsPlace {
  place_id: string;
  name?: string;
  formatted_address?: string;
  international_phone_number?: string;
  formatted_phone_number?: string;
  url?: string;
  rating?: number;
  user_ratings_total?: number;
  geometry?: { location: { lat: number; lng: number } };
  reviews?: Array<{
    author_name: string;
    rating: number;
    text: string;
    language?: string;
    time: number;
  }>;
}

async function adminFetch(path: string) {
  return fetch(`${env.apiBaseUrl}${path}`, { credentials: 'include' });
}

export function useGmapsSearch(q: string) {
  return useQuery({
    queryKey: ['admin', 'gmaps', 'search', q],
    enabled: q.trim().length >= 3,
    queryFn: async (): Promise<{ predictions: GmapsPrediction[]; configured: boolean }> => {
      const res = await adminFetch(`/api/admin/gmaps/search?q=${encodeURIComponent(q)}`);
      if (res.status === 503) return { predictions: [], configured: false };
      if (!res.ok) throw new Error('search_failed');
      const data = (await res.json()) as { predictions: GmapsPrediction[] };
      return { predictions: data.predictions, configured: true };
    },
    staleTime: 60_000,
  });
}

export function useGmapsPlace() {
  return useMutation({
    mutationFn: async (place_id: string): Promise<GmapsPlace> => {
      const res = await adminFetch(`/api/admin/gmaps/place/${encodeURIComponent(place_id)}`);
      if (!res.ok) throw new Error('place_failed');
      const data = (await res.json()) as { place: GmapsPlace };
      return data.place;
    },
  });
}
